import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { FaIconComponent, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faPenToSquare, faTrashCan } from '@fortawesome/pro-regular-svg-icons';

export type IconActionKind = 'edit' | 'delete';

const DEFAULT_LABEL: Record<IconActionKind, string> = { edit: 'Edit', delete: 'Delete' };

/**
 * Admin-portal row action: pencil-in-a-box for edit, trash can for delete, hover text via tooltip.
 * Use instead of text links / kebab menus for edit + delete everywhere in the portal.
 */
@Component({
    selector: 'r1-icon-action',
    template: `
        <button
            mat-icon-button
            type="button"
            class="icon-action"
            [class.delete]="kind() === 'delete'"
            [attr.aria-label]="tooltip()"
            [matTooltip]="tooltip()"
            [disabled]="disabled()"
            (click)="$event.stopPropagation(); action.emit()">
            <fa-icon [icon]="['far', kind() === 'delete' ? 'trash-can' : 'pen-to-square']" class="fa-fw" />
        </button>
    `,
    styles: `
        .icon-action {
            --mat-icon-button-state-layer-size: 32px;
            width: 32px;
            height: 32px;
            padding: 0;
            color: var(--r1-brilliant-blue);
            font-size: 15px;
            &.delete {
                color: var(--r1-red, #ef3742);
            }
            &:disabled {
                color: #bbb;
            }
        }
    `,
    imports: [MatIconButton, MatTooltip, FaIconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconActionComponent {
    kind = input.required<IconActionKind>();
    /** Hover text; defaults to "Edit" / "Delete" */
    label = input<string>();
    disabled = input(false);
    action = output<void>();

    readonly tooltip = computed(() => this.label() ?? DEFAULT_LABEL[this.kind()]);

    constructor() {
        inject(FaIconLibrary).addIcons(faPenToSquare, faTrashCan);
    }
}
